import { Trans } from "@lingui/react/macro";
import { ErrorBoundary } from "react-error-boundary";
import { RouteObject } from "react-router-dom";

import {
  ActionGuard,
  contractSignGuard,
} from "@/common/acl/action-guard/action-guard";
import { primary } from "@/common/components/button/button";
import { Failed as FailedComponent } from "@/common/components/info/info";
import { SearchParamsLink } from "@/common/components/search-params-link/search-params-link";
import { FeatureMode } from "@/domain/plan/feature-mode.service";

import * as PurchasePrices from "./purchase-prices/purchase-prices";
import * as QuickTrading from "./quick-trading/quick-trading";
import * as QuickTradingDetail from "./quick-trading/quick-trading.detail";
import * as QuickTradingOrder from "./quick-trading/quick-trading.detail.order";
import { wrapWithCompanyGuards } from "./routes.utils";
import {
  Params,
  Restoration,
} from "./shared/components/postal-code-dialog/postal-code-restoration";
import { Params as CurrencyParams } from "./shared/currency.params";
import * as Failed from "./shared/routes/failed/failed";
import { ContractLink, OrderLink } from "./shared/routes/success/order-link";
import * as Success from "./shared/routes/success/success";
import { routes as storagesRoutes } from "./storages/storages.routes";
import * as Trades from "./trades/trades";
import * as TradeDetail from "./trades/trades.detail";
import * as TradesDetailOrder from "./trades/trades.detail.order";
import * as TradesDetailSell from "./trades/trades.detail.sell";
import * as TransactionsAndOrders from "./transactions-and-orders/transactions-and-orders";
import * as ContractDetail from "./transactions-and-orders/transactions-and-orders.contract";
import * as OrderDetail from "./transactions-and-orders/transactions-and-orders.order";
import * as Signature from "./transactions-and-orders/transactions-and-orders.signature";

const quickTradingRoutes: RouteObject[] = wrapWithCompanyGuards([
  {
    children: [
      {
        children: [
          {
            children: [
              {
                element: (
                  <Success.Success
                    action={<OrderLink />}
                    content={
                      <Success.Success.Content>
                        <Trans>
                          <b>Váš příkaz byl úspěšně nastaven.</b> Jakmile bude
                          aktivován, budeme vás neprodleně informovat.
                        </Trans>
                      </Success.Success.Content>
                    }
                    title={
                      <Success.Success.Title>
                        <Trans>Příkaz úspěšně nastaven</Trans>
                      </Success.Success.Title>
                    }
                  />
                ),
                path: `:orderId/offer/:offerId/${Success.path}`,
              },
              {
                element: (
                  <Failed.Failed
                    action={
                      <SearchParamsLink className={primary} replace to="..">
                        <Trans>Přejít na detail příkazu</Trans>
                      </SearchParamsLink>
                    }
                    content={
                      <Trans>
                        Bohužel někde nastala chyba a příkaz k prodeji se
                        nepodařilo nastavit. Zkuste to později.
                      </Trans>
                    }
                    title={<Trans>Příkaz se nepodařilo nastavit</Trans>}
                  />
                ),
                path: Failed.path,
              },
            ],
            element: <QuickTradingOrder.Order />,
            path: QuickTradingOrder.path,
          },
          {
            element: (
              <Failed.Failed
                action={null}
                content={
                  <Trans>
                    Bohužel někde nastala chyba a prodej se nepodařilo
                    uskutečnit. Zkuste to později.
                  </Trans>
                }
                title={<Trans>Prodej se nepodařilo uskutečnit</Trans>}
              />
            ),
            path: `sell/${Failed.path}`,
          },
          {
            element: (
              <Success.Success
                action={<ContractLink />}
                content={
                  <Success.Success.Content>
                    <Trans>
                      <b>Transakce byla odeslána k potvrzení.</b> Jakmile bude
                      potvrzena, obratem vám pošleme upozornění a smlouvu k
                      podpisu.
                    </Trans>
                  </Success.Success.Content>
                }
                title={
                  <Success.Success.Title>
                    <Trans>Odesláno k potvrzení</Trans>
                  </Success.Success.Title>
                }
              />
            ),
            path: `sell/${Success.path}/:contractId`,
          },
        ],
        element: (
          <ActionGuard grant="CONTRACT_SIGN" guard={contractSignGuard}>
            <QuickTradingDetail.QuickTradingDetail />
          </ActionGuard>
        ),
        path: QuickTradingDetail.path,
      },
    ],
    element: (
      <ErrorBoundary
        fallbackRender={({ error }) => <FailedComponent error={error} />}
      >
        <Restoration key={QuickTrading.path} path={QuickTrading.path}>
          {(params) => (
            <Params params={params}>
              <QuickTrading.QuickTrading />
            </Params>
          )}
        </Restoration>
      </ErrorBoundary>
    ),
    path: QuickTrading.path,
  },
]);

const getPurchasePricesRoutes = (mode: FeatureMode): RouteObject[] => [
  {
    element: (
      <ErrorBoundary
        fallbackRender={({ error }) => <FailedComponent error={error} />}
      >
        <Restoration key={PurchasePrices.path} path={PurchasePrices.path}>
          {(params) => (
            <Params params={params}>
              <PurchasePrices.PurchasePrices mode={mode} />
            </Params>
          )}
        </Restoration>
      </ErrorBoundary>
    ),
    path: PurchasePrices.path,
  },
];

const tradingRoutes: RouteObject[] = wrapWithCompanyGuards([
  {
    children: [
      {
        children: [
          {
            children: [
              {
                element: <TradesDetailOrder.TradesDetailOrder />,
                path: TradesDetailOrder.path,
              },
            ],
            element: (
              <ActionGuard grant="CONTRACT_SIGN" guard={contractSignGuard}>
                <TradesDetailSell.TradesDetailSell />
              </ActionGuard>
            ),
            path: TradesDetailSell.path,
          },
          {
            element: (
              <Success.Success
                action={<TradesDetailSell.TransactionLink />}
                content={
                  <Success.Success.Content>
                    <Trans>
                      <b>Transakce byla odeslána k potvrzení.</b> Jakmile bude
                      potvrzena, obratem vám pošleme upozornění a smlouvu k
                      podpisu.
                    </Trans>
                  </Success.Success.Content>
                }
                title={
                  <Success.Success.Title>
                    <Trans>Odesláno k potvrzení</Trans>
                  </Success.Success.Title>
                }
              />
            ),
            path: Success.path + "/contract/:contractId",
          },
          {
            element: (
              <Success.Success
                action={<OrderLink />}
                content={
                  <Success.Success.Content>
                    <Trans>
                      <b>Váš příkaz byl úspěšně nastaven.</b> Jakmile bude
                      aktivován, budeme vás neprodleně informovat.
                    </Trans>
                  </Success.Success.Content>
                }
                title={
                  <Success.Success.Title>
                    <Trans>Příkaz úspěšně nastaven</Trans>
                  </Success.Success.Title>
                }
              />
            ),
            path: Success.path + "/order/:orderId",
          },
          {
            element: (
              <Failed.Failed
                action={
                  <SearchParamsLink className={primary} replace to="..">
                    <Trans>Přejít na detail příkazu</Trans>
                  </SearchParamsLink>
                }
                content={
                  <Trans>
                    Bohužel někde nastala chyba a příkaz k prodeji se nepodařilo
                    nastavit. Zkuste to později.
                  </Trans>
                }
                title={<Trans>Příkaz se nepodařilo nastavit</Trans>}
              />
            ),
            path: Failed.path,
          },
        ],
        element: <TradeDetail.TradeDetail />,
        path: TradeDetail.path,
      },
    ],
    element: (
      <ErrorBoundary
        fallbackRender={({ error }) => <FailedComponent error={error} />}
      >
        <CurrencyParams>
          <Trades.Trades />
        </CurrencyParams>
      </ErrorBoundary>
    ),
    path: Trades.path,
  },
]);

const transactionsAndOrdersRoutes: RouteObject[] = wrapWithCompanyGuards([
  {
    children: [
      {
        children: [
          {
            element: (
              <Success.Success
                action={<ContractLink />}
                content={
                  <Success.Success.Content>
                    <Trans>
                      <b>
                        Smlouva byla úspěšně podepsána a prodej je potvrzen.
                      </b>{" "}
                      Detail transakce včetně dokumentů naleznete v detailu
                      transakce.
                    </Trans>
                  </Success.Success.Content>
                }
                title={
                  <Success.Success.Title>
                    <Trans>Prodej dokončen!</Trans>
                  </Success.Success.Title>
                }
              />
            ),
            path: Success.path,
          },
          {
            element: <Signature.Signature />,
            path: Signature.path,
          },
        ],
        element: <ContractDetail.Contract />,
        path: ContractDetail.path,
      },
      {
        children: [
          {
            element: (
              <Success.Success
                action={<OrderLink />}
                content={
                  <Success.Success.Content>
                    <Trans>
                      <b>Váš příkaz byl úspěšně nastaven.</b> Jakmile bude
                      aktivován, budeme vás neprodleně informovat.
                    </Trans>
                  </Success.Success.Content>
                }
                title={
                  <Success.Success.Title>
                    <Trans>Příkaz úspěšně nastaven</Trans>
                  </Success.Success.Title>
                }
              />
            ),
            path: Success.path,
          },
          {
            element: (
              <Failed.Failed
                action={
                  <SearchParamsLink className={primary} replace to="..">
                    <Trans>Přejít na detail příkazu</Trans>
                  </SearchParamsLink>
                }
                content={
                  <Trans>
                    Bohužel někde nastala chyba a příkaz k prodeji se nepodařilo
                    nastavit. Zkuste to později.
                  </Trans>
                }
                title={<Trans>Příkaz se nepodařilo nastavit</Trans>}
              />
            ),
            path: Failed.path,
          },
        ],
        element: (
          <ActionGuard grant="CONTRACT_SIGN" guard={contractSignGuard}>
            <OrderDetail.Order />
          </ActionGuard>
        ),
        path: OrderDetail.path,
      },
    ],
    element: (
      <TransactionsAndOrders.Params>
        <TransactionsAndOrders.TransactionsAndOrders />
      </TransactionsAndOrders.Params>
    ),
    path: TransactionsAndOrders.path,
  },
]);

export {
  getPurchasePricesRoutes,
  quickTradingRoutes,
  storagesRoutes,
  tradingRoutes,
  transactionsAndOrdersRoutes,
};
